import api from "./api";

/**
 * The duplicate-grounds report's client half (issue #132).
 *
 * Presentation only — no scoring happens here and none ever should. The
 * bands, the distances and the similarity are computed once, on the server
 * (`api/src/lib/groundSimilarity.ts`), because a heuristic implemented
 * twice is a heuristic that disagrees with itself the first time either
 * copy is tuned. This module fetches that answer and decides how to say it.
 */

/** `GET /api/admin/grounds/duplicates` — admin-only. */
export async function fetchDuplicateGroundReport() {
  const { data } = await api.get("/admin/grounds/duplicates");
  return data;
}

/**
 * Issue #407 — "I looked at these two and they are genuinely different
 * fields." The pair goes in either order; the server keys it on the pair.
 *
 * NOT THE MERGE, WHICH LIVES IN `lib/groundMerge.js` (#618). A dismissal
 * changes no ground: it records a person's judgement, it is undone by
 * `restoreDuplicatePair` below, and it expires by itself if either ground is
 * edited into something the judgement was not about. The merge repoints
 * other people's bookmarks and has no undo, so it is a separate module with
 * a survivor rule of its own — kept apart on purpose, so that neither
 * verdict's client can be reached by a change aimed at the other.
 */
export async function dismissDuplicatePair(groundAId, groundBId, note) {
  const { data } = await api.post("/admin/grounds/duplicates/dismissals", {
    ground_a_id: groundAId,
    ground_b_id: groundBId,
    ...(note ? { note } : {}),
  });
  return data;
}

/** Undo a dismissal — the pair is proposed again on the next scan. */
export async function restoreDuplicatePair(groundAId, groundBId) {
  const { data } = await api.delete(
    `/admin/grounds/duplicates/dismissals/${encodeURIComponent(groundAId)}/${encodeURIComponent(groundBId)}`,
  );
  return data;
}

/**
 * Strongest evidence first, and every band described in the words a
 * reviewer needs rather than the words the scorer uses.
 *
 * The blurbs matter more than they look. A band called "possible" with no
 * explanation reads as "the tool is not sure", and a reviewer starts
 * merging things to clear the list. It is not uncertainty — it is the band
 * that deliberately holds two real, distinct grounds at one venue, and
 * saying so is what stops someone merging them.
 */
export const BANDS = [
  {
    key: "near-certain",
    label: "Near certain",
    blurb: "Same name once spelling and accents are set aside, and less than 200 m apart.",
    tone: "bg-rose-50 text-rose-700 border-rose-200",
  },
  {
    key: "likely",
    label: "Likely",
    blurb:
      "Very close names within 500 m, or the same name within 2 km — often one club with a pin dropped on the car park.",
    tone: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    key: "possible",
    label: "Possible",
    blurb:
      "Worth a look, and where the false positives live. Two genuinely different grounds at one venue land here on purpose — check the names before merging anything.",
    tone: "bg-slate-100 text-slate-600 border-slate-200",
  },
];

export function bandMeta(key) {
  return BANDS.find((b) => b.key === key) ?? BANDS[BANDS.length - 1];
}

/** Group the flat pair list into the three bands, in order, keeping empty
 * bands out of the render (their counts are reported in the summary, so an
 * absent section reads as "none", not as "not computed"). */
export function groupByBand(pairs) {
  return BANDS.map((band) => ({
    ...band,
    pairs: (pairs ?? []).filter((p) => p.band === band.key),
  })).filter((g) => g.pairs.length > 0);
}

/**
 * Takes METRES. Metres up to a kilometre, then one decimal of a kilometre.
 *
 * No sub-metre precision anywhere: these pins were dropped by thumb on a
 * phone map, and "38.4 m" would claim an accuracy the input never had.
 *
 * Issue #436 item 1: named for its unit because `distance.js`, in this same
 * directory, exports `formatKm`, which takes kilometres. Both were called
 * `formatDistance`, and neither would have complained about being handed
 * the other's number. See `formatKm` for the full argument.
 */
export function formatMetres(metres) {
  if (metres === null || metres === undefined || !Number.isFinite(metres)) return "—";
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(1)} km`;
}

/**
 * One sentence explaining why the pair is on the screen — the "searching
 * it" half of Martin's ask, answered by the report itself rather than by a
 * model. A reviewer who has to reverse-engineer the scorer to read a row
 * will stop reading rows.
 */
export function explainPair(pair) {
  const distance = formatMetres(pair.distance_metres);
  const base = pair.names_match
    ? `Identical names once spelling and accents are set aside, ${distance} apart.`
    : `Names score ${Math.round((pair.name_similarity ?? 0) * 100)}% alike, ${distance} apart.`;
  if (pair.capped_by === "designator") {
    return `${base} Held at "possible" because the names disagree about a number or letter (No.1 vs No.2), which usually means two different grounds at one venue.`;
  }
  if (pair.reason === "same-spot") {
    return `${base} Flagged on the pins alone — the names are not alike.`;
  }
  return base;
}

/** "47 public grounds scanned, 1,081 pairs compared" — the denominator, so
 * an empty report can be told apart from a report that loaded nothing. */
export function formatScanSummary(report) {
  if (!report) return "";
  const grounds = report.rows_scanned ?? 0;
  const pairs = report.pairs_compared ?? 0;
  return `${grounds.toLocaleString("en-NZ")} public ${grounds === 1 ? "ground" : "grounds"} scanned, ${pairs.toLocaleString("en-NZ")} ${pairs === 1 ? "pair" : "pairs"} compared`;
}

/**
 * Issue #619 — WHAT IS IN FRONT OF THE REVIEWER, AND WHAT IS BEING HELD
 * BACK, in one line at the top of the page.
 *
 * The ruled-out pairs collapsed (`AdminGroundDuplicates.jsx`), and #407's
 * guarantee — a suppression nobody can see is indistinguishable from a scan
 * that missed something — cannot depend on a section the reader has to
 * expand. So the count moves to the scan summary, where it is on screen
 * without expanding anything. That, not the card list, is what makes the
 * guarantee true.
 *
 * "3 held back" is appended only when something IS held back. A "0 held
 * back" on every clean report would be noise standing in for a fact the
 * reader can already see (no section), and the failure mode being guarded
 * against only exists when the number is not zero.
 */
export function formatReportCounts(report) {
  if (!report) return "";
  const pairs = report.pairs?.length ?? 0;
  const held = report.dismissed?.length ?? 0;
  const reviewing = `${pairs.toLocaleString("en-NZ")} ${pairs === 1 ? "pair" : "pairs"} to review`;
  return held > 0 ? `${reviewing} · ${held.toLocaleString("en-NZ")} held back` : reviewing;
}

/**
 * "Ruled out by Andreas on 3 Aug 2026" — the audit trail in the words a
 * reader needs (#407 decision 3).
 *
 * The NAME comes from `decided_by_name`, which the server denormalised when
 * the verdict was written, so it still reads as a person after the account
 * is gone. A verdict with no date is not rendered as "on Invalid Date": the
 * date is dropped and the sentence still says who.
 */
export function formatVerdict(verdict) {
  if (!verdict) return "";
  const who = verdict.decided_by_name || "an admin";
  const when = verdict.decided_at ? new Date(verdict.decided_at) : null;
  if (!when || Number.isNaN(when.getTime())) return `Ruled out by ${who}`;
  return `Ruled out by ${who} on ${when.toLocaleDateString("en-NZ", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
}

/**
 * The sentence a pair carries when its dismissal has EXPIRED — it was ruled
 * out, one of the two grounds has been edited since, and it is back in front
 * of a reviewer on purpose.
 *
 * Saying this out loud is the whole reason the expiring verdict beats a
 * permanent one. A pair that silently reappeared would read as the report
 * having forgotten, which is what teaches people to stop dismissing.
 */
export function formatStaleVerdict(verdict) {
  if (!verdict) return "";
  return `${formatVerdict(verdict)} — one of these has been edited since, so it is back for another look.`;
}
